<template>
  <div v-if="currentUser && expenses.length !== 0" class="expenses-listing">
    <ExpensesFilters
      v-if="!hideHeaders"
      :expenses-assignments="expensesAssignments"
      :expenses-contractors="expensesContractors"
      :expenses-filters="expensesFilters"
      :current-user="currentUser"
      @change="onFiltersChange"
    />

    <TolAlert v-if="tolAlert" v-bind="tolAlert" />

    <ExpensesActions :show-bulk-action="showCheckbox" :title-all-or-filtered="allOrFilteredTitle" />

    <tol-table
      :table-data="expenses"
      :columns="columns"
      :show-spinner="isLoading"
      :is-row-clickable="true"
      @row-click="onRowClick($event)"
    >
      <template v-slot:tableHeader>
        <th v-if="showCheckbox"></th>
        <th v-if="canViewPayrollNumber">{{ $t('expenses.listing.labels.payrollNumber') }}</th>

        <th-sortable
          v-if="canViewContractor"
          :direction="currentSortField === 'contractorName' ? currentSortDirection : 'none'"
          field-name="contractorName"
          @change="onSortOrderChange"
          >{{ $t('expenses.listing.labels.contractor') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'client' ? currentSortDirection : 'none'"
          field-name="client"
          @change="onSortOrderChange"
          >{{ $t('expenses.listing.labels.client') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'startDate' ? currentSortDirection : 'none'"
          field-name="startDate"
          @change="onSortOrderChange"
          >{{ $t('expenses.listing.labels.startDate') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'endDate' ? currentSortDirection : 'none'"
          field-name="endDate"
          @change="onSortOrderChange"
          >{{ $t('expenses.listing.labels.endDate') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'totalAmount' ? currentSortDirection : 'none'"
          field-name="totalAmount"
          @change="onSortOrderChange"
          >{{ '$ ' + $t('expenses.listing.labels.totalAmount') }}</th-sortable
        >

        <th>{{ $t('expenses.listing.labels.status') }}</th>
      </template>

      <template v-if="showCheckbox" v-slot:check="{ value }">
        <td @click.stop>
          <Checkbox
            size="small"
            :checked="value.isSelected"
            :modified="value.referenceNumber in modifiedExpenses"
            @change="onToggleSelectExpense(value.referenceNumber)"
          />
        </td>
      </template>

      <template v-slot:referenceNumber="{ value }">
        <router-link :to="value.referenceNumber">{{ value.referenceNumber }}</router-link>
      </template>

      <template v-slot:contractor="{ value }">{{ value.contractorName }}</template>

      <template v-slot:startDate="{ value }">
        <font-awesome-icon icon="calendar-alt" />
        {{ value.startDate }}
      </template>

      <template v-slot:endDate="{ value }">
        <font-awesome-icon icon="calendar-alt" />
        {{ value.endDate }}
      </template>

      <template v-slot:totalAmount="{ value }"
        >${{ value.totalAmount }}</template
      >

      <template v-slot:status="{ value }">
        <Status :status="value.status" object-type="expense" />
      </template>
    </tol-table>
    <Pagination
      :records-per-page="expensesPerPage"
      :offset="currentPage"
      :record-count="recordCount"
      @change="onChangePaginationParams($event)"
    />
  </div>
</template>

<script>
import { ExpensesListingActions } from '@/app/features/expenses/features/expenses-listing/expenses-listing.keys';
import { ExpensesListingGetters } from './expenses-listing.keys';
import TolAlert from '@/app/components/TolAlert';
import ExpensesFilters from '@/app/features/expenses/features/expenses-listing/components/ExpensesFilters';
import ExpensesActions from '@/app/features/expenses/features/expenses-listing/components/ExpensesActions';
import { AppActions } from '@/app/app.keys';
import TolTable from '@/app/components/TolTable';
import Status from '@/app/components/Status';
import Checkbox from '@/app/components/Checkbox';
import ThSortable from '@/app/components/ThSortable';
import Pagination from '@/app/components/Pagination';
import { userUtils } from '@/mixins/user-utils';

export default {
  name: 'ExpensesListing',
  components: {
    TolAlert,
    ExpensesFilters,
    TolTable,
    ExpensesActions,
    Status,
    ThSortable,
    Checkbox,
    Pagination
  },
  mixins: [userUtils],
  props: {
    hideHeaders: {
      type: Boolean,
      default: false
    },
    userOnHomePage: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isLoading() {
      return this.$store.getters[ExpensesListingGetters.ExpenseIsLoading];
    },

    expensesPerPage: {
      get() {
        return this.$store.getters.ExpensesPerPage;
      }
    },
    currentPage: {
      get() {
        return this.$store.getters.ExpensesListingOffset;
      }
    },
    recordCount: {
      get() {
        return +this.$store.getters.ExpensesFilteredSortedRecordCount;
      }
    },
    expensesAssignments: function() {
      return this.$store.state.expensesState.expensesListingState.expensesAssignments;
    },

    expensesContractors: function() {
      return this.$store.getters[ExpensesListingGetters.ExpensesContractors];
    },

    currentUser: function() {
      return this.$store.state.currentUser;
    },

    expensesFilters: function() {
      return this.$store.state.expensesState.expensesListingState.expensesFilters;
    },

    expenses: function() {
      return this.$store.state.expensesState.expensesListingState.expenses;
    },

    expenseStatusCounts() {
      return this.$store.state.expensesState.expensesListingState.expensesStatusCounts;
    },

    tolAlert() {
      if (this.currentUser === null || this.expenseStatusCounts === null) {
        return null;
      }

      if (this.isClientContact && this.expenseStatusCounts.submitted > 0) {
        let count = this.expenseStatusCounts.submitted;
        let status = this.$t('statuses.submitted');
        let message = this.$tc('expenses.listing.alertMessage', count, { count, status });
        return { message, type: 'warning' };
      }

      if (this.isContractor && this.expenseStatusCounts.rejected > 0) {
        let count = this.expenseStatusCounts.rejected;
        let status = this.$t('statuses.rejected');
        let message = this.$tc('expenses.listing.alertMessage', count, { count, status });
        return { message, type: 'error' };
      }

      return null;
    },
    canViewContractor() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['CONTRACTOR'] &&
        !!this.currentUser.permissions['CONTRACTOR']['VIEW_IN_LIST']
      );
    },
    canViewPayrollNumber() {
      //only TES employess can create time sheets, and so will see payrol number in listing
      return !!this.currentUser && !!this.currentUser.permissions['TIME_SHEET']['CREATE'];
    },
    columns() {
      let tableColumns = ['client', 'startDate', 'endDate', 'totalAmount', 'status'];
      if (this.canViewContractor) tableColumns.splice(0, 0, 'contractor');

      if (this.canViewPayrollNumber) tableColumns.splice(0, 0, 'payrollNumber');

      return tableColumns;
    },
    showCheckbox() {
      let role = this.currentUser.roles.find(
        role =>
          role.role_name == 'Client Contact' ||
          role.role_name == 'TES Approver' ||
          role.role_name === 'System Admin'
      );
      return role ? true : false;
    },
    hasExpenses() {
      return this.expenses.length > 0;
    },
    currentSortField() {
      return this.$store.state.expensesState.expensesListingState.expensesSortOrder.sortBy;
    },
    currentSortDirection() {
      return this.$store.state.expensesState.expensesListingState.expensesSortOrder.sortDirection;
    },
    modifiedExpenses() {
      return this.$store.getters.ModifiedExpenses;
    },
    allOrFilteredTitle() {
      return this.areAnyFiltersSet(this.expensesFilters)
        ? 'expenses.listing.actions.titleFiltered'
        : this.hideHeaders
        ? 'expenses.listing.actions.titleYour'
        : 'expenses.listing.actions.titleAll';
    }
  },
  mounted: function() {
    if (this.userOnHomePage) {
      this.$store.dispatch(AppActions.SetPageTitle, this.$t('app.homePage.title'));
    } else {
      this.$store.dispatch(AppActions.SetPageTitle, this.$t('expenses.listing.title'));
    }
    this.$store.dispatch(ExpensesListingActions.RetrieveExpenses);
    this.$store.dispatch(ExpensesListingActions.RetrieveExpensesStatusCounts);
  },
  methods: {
    areAnyFiltersSet(expensesFilters) {
      return (
        expensesFilters.assignmentId ||
        expensesFilters.contractorId ||
        expensesFilters.dateRangeFilters.startDate != null ||
        expensesFilters.dateRangeFilters.endDate != null ||
        Object.values(expensesFilters.statusFilters).some(val => val)
      );
    },
    onFiltersChange(expensesFilters) {
      this.$store.dispatch(ExpensesListingActions.SetExpensesFilters, expensesFilters);
    },
    onSortOrderChange(sortOrder) {
      this.$store.dispatch(ExpensesListingActions.SetExpensesSortOrder, sortOrder);
    },
    onToggleSelectExpense(event) {
      //Add selected expense to selectedExpenses in store
      this.$store.dispatch(ExpensesListingActions.SelectExpense, event);
    },
    onChangePaginationParams(event) {
      this.$store.dispatch(ExpensesListingActions.SetExpensesPaginationParameters, event);
    },
    onRowClick(evt) {
      this.$router.push(`/expenses/${evt.referenceNumber}`);
    }
  }
};
</script>
